
import React, { useEffect, useRef, useState } from "react";
import { clearAuthToken } from "../../data/interface/authStorage";
import { clearUser } from "../../data/redux/authSlice";
import { clearPermissions } from "../../data/redux/permissionsSlice";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ImageWithBasePath from "../imageWithBasePath";
import { all_routes } from "../../../feature-module/router/all_routes";
import { useDispatch, useSelector } from "react-redux";
import {
  setExpandMenu,
  setMiniSidebar,
  setMobileSidebar,
} from "../../data/redux/commonSlice";
import { useNotifications } from "../../../../src/feature-module/notification/services/useNotifications"; // Add this import

import { HorizontalSidebarData } from "../../data/json/horizontalSidebar";
import LanguageSwitcher from "./LanguageSwitcher";
import CompanySwitcher from "./CompanySwitcher";

const Header = () => {
  const { t } = useTranslation("header");
  const [subOpen, setSubopen] = useState<any>("");
  const [subsidebar, setSubsidebar] = useState("");
  const navigate = useNavigate();
  const [isFullscreen, setIsFullscreen] = useState(false);
  const route = all_routes;
  const location = useLocation();
  const dispatch = useDispatch();
  const mobileSidebar = useSelector(
    (state: any) => state.commonSlice.mobileSidebar
  );
  // Tenancy: display-only, never sent back as a filter (§5.1).
  const tenantName = useSelector((state: any) => state.auth?.user?.tenantName);
  const miniSidebar = useSelector(
    (state: any) => state.commonSlice.miniSidebar
  );
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [showNotificationDropdown, setShowNotificationDropdown] = useState(false);
  const dropdownRef = useRef<HTMLLIElement>(null); // Changed to HTMLLIElement
  const notificationDropdownRef = useRef<HTMLLIElement>(null);

  const {
    notifications,
    unreadCount,
    connectionState,
    markAsRead,
    markAllAsRead,
    dismissNotification,
    formatNotificationTime,
    loadNotifications
  } = useNotifications({ lazy: true, limit: 10 }); // Lazy load with limit of 10 notifications

  const mapTemplateToRoute = (templateName: string, typeName?: string): string | null => {
    const routes: Record<string, string> = {
      "Training Expired": "/training-list",
      "Training Expiry Reminder": "/training-list",
      "NCR Report Submission": "/ncr-submission",
      "Holiday Request": "/HolidayRequest-list",
      "Clothing Request": "/ClothingRequest-list",
      "Clothing Return": "/ClothingReturn-list",
      "Customer Request Report": "/reports/all-reports",
      "Certification Expired": "/Certifications",
      "Certification Expiry Reminder": "/Certifications",
      "Audit Scheduled": "/audit",
      "Order Deleted Template": "/kanbanview",
      "Order Creation Template": "/kanbanview",
    };
    // Exact match
    if (templateName && routes[templateName]) return routes[templateName];
    // Partial match on templateName
    if (templateName) {
      const key = Object.keys(routes).find(k => templateName.toLowerCase().includes(k.toLowerCase()));
      if (key) return routes[key];
    }
    // Fallback: match on typeName
    if (typeName) {
      const key = Object.keys(routes).find(k => typeName.toLowerCase().includes(k.toLowerCase().replace(" template", "").replace("request", "").trim()));
      if (key) return routes[key];
    }
    return null;
  };

  const navigateWindow = async (notification: any) => {
    await markAsRead(notification.id);
    setShowNotificationDropdown(false);
    const route = mapTemplateToRoute(notification.templateName, notification.typeName);
    if (route) navigate(route);
    // else: no matching route — mark as read silently, no navigation
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowProfileDropdown(false);
      }
      if (
        notificationDropdownRef.current &&
        !notificationDropdownRef.current.contains(event.target as Node)
      ) {
        setShowNotificationDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleNotificationDropdownToggle = () => {
    const newState = !showNotificationDropdown;
    setShowNotificationDropdown(newState);
    if (newState) {
      loadNotifications();
    }
  };

  const toggleMobileSidebar = () => {
    dispatch(setMobileSidebar(!mobileSidebar));
  };

  const isLockScreen = location.pathname === "/lock-screen";

  if (isLockScreen) {
    return null;
  }

  const toggleSidebar = (title: any) => {
    localStorage.setItem("menuOpened", title);
    if (title === subOpen) {
      setSubopen("");
    } else {
      setSubopen(title);
    }
  };

  const toggleSubsidebar = (subitem: any) => {
    if (subitem === subsidebar) {
      setSubsidebar("");
    } else {
      setSubsidebar(subitem);
    }
  };

  const toggleFullscreen = () => {
    if (!isFullscreen) {
      if (document.documentElement.requestFullscreen) {
        document.documentElement.requestFullscreen().catch((err) => { });
        setIsFullscreen(true);
      }
    } else {
      if (document.exitFullscreen) {
        if (document.fullscreenElement) {
          document.exitFullscreen().catch((err) => { });
        }
        setIsFullscreen(false);
      }
    }
  };

  return (
    <>
      {/* Header */}
      <div className="header">
        {/* Logo */}
        <div
          className="header-left active"
          // Removed onMouseEnter and onMouseLeave to disable hover expand/collapse
          style={{
            display: "flex",
            alignItems: "center",
            flexDirection: miniSidebar ? "row" : "initial",
          }}
        >
          {miniSidebar ? (
            <>
              <img
                src="/assets/img/favicon.png"
                alt="Logo"
                style={{ width: 32, height: 32, marginLeft: 20 }}
              />
              <Link
                id="toggle_btn"
                to="#"
                onClick={() => {
                  if (miniSidebar) {
                    dispatch(setMiniSidebar(false));
                    dispatch(setExpandMenu(false));
                  } else {
                    dispatch(setMiniSidebar(true));
                    dispatch(setExpandMenu(false));
                  }
                }}
                title={miniSidebar ? t("sidebar.expand") : t("sidebar.collapse")}
                aria-label={miniSidebar ? "Expand sidebar" : "Collapse sidebar"}
              >
                <i
                  className={`ti ${miniSidebar
                    ? "ti-arrow-bar-to-right"
                    : "ti-arrow-bar-to-left"
                    }`}
                />
              </Link>
            </>
          ) : (
            <>
              <Link to={route.dealsDashboard} className="logo logo-normal">
                <ImageWithBasePath
                  src="assets/img/CM Central Logo (1200 x 700 px).png"
                  alt="Logo"
                />
                <ImageWithBasePath
                  src="assets/img/CM Central Logo (1200 x 700 px).png"
                  className="white-logo"
                  alt="Logo"
                />
              </Link>
              <Link to={route.dealsDashboard} className="logo-small">
                <ImageWithBasePath
                  src="assets/img/CM Central Logo (1200 x 700 px).png"
                  alt="Logo"
                />
              </Link>
              <Link
                id="toggle_btn"
                to="#"
                onClick={() => {
                  if (miniSidebar) {
                    dispatch(setMiniSidebar(false));
                    dispatch(setExpandMenu(false));
                  } else {
                    dispatch(setMiniSidebar(true));
                    dispatch(setExpandMenu(false));
                  }
                }}
                title={miniSidebar ? t("sidebar.expand") : t("sidebar.collapse")}
                aria-label={miniSidebar ? "Expand sidebar" : "Collapse sidebar"}
              >
                <i
                  className={`ti ${miniSidebar
                    ? "ti-arrow-bar-to-right"
                    : "ti-arrow-bar-to-left"
                    }`}
                />
              </Link>
            </>
          )}
        </div>
        {/* /Logo */}
        <Link
          id="mobile_btn"
          className="mobile_btn"
          to="#sidebar"
          onClick={toggleMobileSidebar}
        >
          <span className="bar-icon">
            <span />
            <span />
            <span />
          </span>
        </Link>
        <div className="header-user">
          <ul className="nav user-menu">
            {/* Search */}
            <li className="nav-item nav-search-inputs me-auto">
              <div className="top-nav-search">
                <Link to="#" className="responsive-search">
                  <i className="fa fa-search" />
                </Link>
                <form className="dropdown">
                  <div className="searchinputs" id="dropdownMenuClickable">
                    <input type="text" placeholder="Search" />
                    <div className="search-addon">
                      <button type="submit">
                        <i className="ti ti-command" />
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </li>

            {/* /Search */}
            <li>
              <div
                className="sidebar sidebar-horizontal"
                id="horizontal-single"
              >
                <div className="sidebar-menu">
                  <div className="main-menu">
                    <ul className="nav-menu">
                      <li className="menu-title">
                        <span>{t("sidebar.main")}</span>
                      </li>
                      {HorizontalSidebarData?.map((mainMenu, index) => (
                        <React.Fragment key={`main-${index}`}>
                          {mainMenu?.menu?.map((data, i) => (
                            <li className="submenu" key={`menu-${i}`}>
                              <Link
                                to="#"
                                className={`
														${data?.subMenus
                                    ?.map((link: any) => link?.route)
                                    .includes(location.pathname)
                                    ? "active"
                                    : ""
                                  } ${subOpen === data.menuValue ? "subdrop" : ""}`}
                                onClick={() => toggleSidebar(data.menuValue)}
                              >
                                <i className={`ti ti-${data.icon}`}></i>
                                <span>{data.menuValue}</span>
                                <span className="menu-arrow"></span>
                              </Link>

                              {/* First-level Submenus */}
                              <ul
                                style={{
                                  display:
                                    subOpen === data.menuValue
                                      ? "block"
                                      : "none",
                                }}
                              >
                                {data?.subMenus?.map((subMenu: any, j) => (
                                  <li
                                    key={`submenu-${j}`}
                                    className={
                                      subMenu?.customSubmenuTwo ? "submenu" : ""
                                    }
                                  >
                                    <Link
                                      to={subMenu?.route || "#"}
                                      className={`${subMenu?.subMenusTwo
                                        ?.map((link: any) => link?.route)
                                        .includes(location.pathname) ||
                                        subMenu?.route === location.pathname
                                        ? "active"
                                        : ""
                                        } ${subsidebar === subMenu.menuValue
                                          ? "subdrop"
                                          : ""
                                        }`}
                                      onClick={() =>
                                        toggleSubsidebar(subMenu.menuValue)
                                      }
                                    >
                                      <span>{subMenu?.menuValue}</span>
                                      {subMenu?.customSubmenuTwo && (
                                        <span className="menu-arrow"></span>
                                      )}
                                    </Link>

                                    {/* Check if `customSubmenuTwo` exists */}
                                    {subMenu?.customSubmenuTwo &&
                                      subMenu?.subMenusTwo && (
                                        <ul
                                          style={{
                                            display:
                                              subsidebar === subMenu.menuValue
                                                ? "block"
                                                : "none",
                                          }}
                                        >
                                          {subMenu.subMenusTwo.map(
                                            (subMenuTwo: any, k: number) => (
                                              <li key={`submenu-two-${k}`}>
                                                <Link
                                                  className={
                                                    subMenuTwo.route ===
                                                      location.pathname
                                                      ? "active"
                                                      : ""
                                                  }
                                                  to={subMenuTwo.route}
                                                >
                                                  {subMenuTwo.menuValue}
                                                </Link>
                                              </li>
                                            )
                                          )}
                                        </ul>
                                      )}
                                  </li>
                                ))}
                              </ul>
                            </li>
                          ))}
                        </React.Fragment>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </li>

            {/* Nav List */}
            <li className="nav-item nav-list">
              <ul className="nav">
                <li>
                  <Link
                    to="#"
                    onClick={toggleFullscreen}
                    className="btn btn-icon border btn-menubar btnFullscreen"
                  >
                    <i className="ti ti-maximize"></i>
                  </Link>
                </li>
{/* 
                <li className="dark-mode-list">
                  <Link
                    to="#"
                    className={`dark-mode-toggle ${layoutBs ? "" : "active"}`}
                    id="dark-mode-toggle"
                  >
                    <i
                      className={`ti ti-sun light-mode ${layoutBs === "dark" ? "" : "active"
                        }`}
                      onClick={LayoutLight}
                    >
                      {" "}
                    </i>
                    <i
                      className={`ti ti-moon dark-mode ${layoutBs === "dark" ? "active" : ""
                        }`}
                      onClick={LayoutDark}
                    ></i>
                  </Link>
                </li> */}
                <li className="nav-item dropdown">
                  <Link to={route.kanbanview} className="btn btn-header-list">
                    <i className="ti ti-layout-grid-add" />
                  </Link>
                </li>
              </ul>
            </li>

            {/* Company the session is in; switcher for multi-company logins */}
            <CompanySwitcher />

            {/* Notifications */}
            <LanguageSwitcher />
            <li className="nav-item dropdown nav-item-box" ref={notificationDropdownRef}>
              <Link
                to="#"
                className="nav-link"
                onClick={(e) => {
                  e.preventDefault();
                  handleNotificationDropdownToggle();
                }}
              >
                <i className="ti ti-bell" />
                {unreadCount > 0 && (
                  <span className="badge rounded-pill bg-danger">
                    {unreadCount}
                  </span>
                )}
                {connectionState !== 'connected' && (
                  <span className="pulse-danger" title="Connection lost"></span>
                )}
              </Link>
              <div className={`dropdown-menu dropdown-menu-end notification-dropdown ${showNotificationDropdown ? "show" : ""}`}
                style={{width:400, borderRadius:16, border:"1px solid #e2e8f0", boxShadow:"0 20px 60px rgba(0,0,0,0.15)", padding:0, overflow:"hidden"}}>
                {/* Header */}
                <div style={{padding:"18px 20px 14px", borderBottom:"1px solid #f1f5f9", display:"flex", alignItems:"center", justifyContent:"space-between"}}>
                  <div style={{display:"flex", alignItems:"center", gap:10}}>
                    <div style={{width:32, height:32, borderRadius:10, background:"#fff7ed", display:"flex", alignItems:"center", justifyContent:"center"}}>
                      <i className="ti ti-bell" style={{fontSize:16, color:"#e85b28"}}></i>
                    </div>
                    <h6 style={{margin:0, fontWeight:700, fontSize:15, color:"#1e293b"}}>{t("notifications.title")}</h6>
                    {unreadCount > 0 && (
                      <span style={{background:"#e85b28", color:"#fff", fontSize:11, fontWeight:700, padding:"2px 8px", borderRadius:20, minWidth:22, textAlign:"center"}}>{unreadCount}</span>
                    )}
                  </div>
                  {unreadCount > 0 && (
                    <button
                      style={{background:"none", border:"none", color:"#e85b28", fontSize:12, fontWeight:600, cursor:"pointer", padding:0}}
                      onClick={(e) => { e.preventDefault(); markAllAsRead(); }}
                    >
                      {t("notifications.markAllRead")}
                    </button>
                  )}
                </div>
                {/* Content */}
                <div style={{maxHeight:380, overflowY:"auto", padding:"4px 0"}}>
                  {!notifications || notifications.length === 0 ? (
                    <div style={{padding:"48px 20px", textAlign:"center"}}>
                      <div style={{width:56, height:56, borderRadius:"50%", background:"#f8fafc", display:"inline-flex", alignItems:"center", justifyContent:"center", marginBottom:12}}>
                        <i className="ti ti-bell-off" style={{fontSize:24, color:"#94a3b8"}}></i>
                      </div>
                      <p style={{margin:0, color:"#94a3b8", fontSize:13, fontWeight:500}}>{t("notifications.empty")}</p>
                    </div>
                  ) : (
                    notifications.map((notification) => (
                      <div
                        key={notification.id}
                        onClick={(e) => { e.preventDefault(); navigateWindow(notification); }}
                        style={{
                          padding:"12px 20px", cursor:"pointer", display:"flex", gap:12, alignItems:"flex-start",
                          borderLeft: !notification.isRead ? "3px solid #e85b28" : "3px solid transparent",
                          background: !notification.isRead ? "#fffbf7" : "transparent",
                          transition:"all 0.15s ease",
                        }}
                        onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.background = "#f8fafc"; }}
                        onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.background = !notification.isRead ? "#fffbf7" : "transparent"; }}
                      >
                        {/* Icon */}
                        <div style={{
                          width:36, height:36, borderRadius:10, flexShrink:0,
                          background: notification.typeName === "HolidayRequest" ? "#fef3c7" :
                                      notification.typeName === "ClothingRequest" ? "#dbeafe" :
                                      notification.typeName === "ClothingReturn" ? "#fce7f3" :
                                      "#fff7ed",
                          display:"flex", alignItems:"center", justifyContent:"center",
                        }}>
                          <i className={
                            notification.typeName === "HolidayRequest" ? "ti ti-beach" :
                            notification.typeName === "ClothingRequest" ? "ti ti-shirt" :
                            notification.typeName === "ClothingReturn" ? "ti ti-refresh" :
                            "ti ti-bell"
                          } style={{
                            fontSize:16,
                            color: notification.typeName === "HolidayRequest" ? "#f59e0b" :
                                   notification.typeName === "ClothingRequest" ? "#3b82f6" :
                                   notification.typeName === "ClothingReturn" ? "#ec4899" :
                                   "#e85b28",
                          }}></i>
                        </div>
                        {/* Content */}
                        <div style={{flex:1, minWidth:0}}>
                          <p style={{margin:"0 0 3px", fontWeight:600, fontSize:13, color:"#1e293b", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>
                            {notification.title || t("notifications.newNotification")}
                          </p>
                          <p style={{margin:"0 0 4px", fontSize:12, color:"#64748b", lineHeight:1.4, display:"-webkit-box", WebkitLineClamp:2, WebkitBoxOrient:"vertical", overflow:"hidden"}}>
                            {notification.body}
                          </p>
                          <span style={{fontSize:11, color:"#94a3b8", fontWeight:500}}>
                            {formatNotificationTime(notification.createdAt)}
                          </span>
                        </div>
                        {/* Dismiss */}
                        <button
                          style={{background:"none", border:"none", padding:4, cursor:"pointer", borderRadius:6, color:"#cbd5e1", flexShrink:0, marginTop:2}}
                          onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.color = "#ef4444"; (e.currentTarget as HTMLButtonElement).style.background = "#fef2f2"; }}
                          onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.color = "#cbd5e1"; (e.currentTarget as HTMLButtonElement).style.background = "none"; }}
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); dismissNotification(notification.id); }}
                        >
                          <i className="ti ti-x" style={{fontSize:14}}></i>
                        </button>
                      </div>
                    ))
                  )}
                </div>
                {/* Footer */}
                <div style={{padding:"12px 20px", borderTop:"1px solid #f1f5f9", display:"flex", alignItems:"center", justifyContent:"space-between", background:"#fafbfc"}}>
                  <Link
                    to={route.AllNotifications}
                    onClick={() => setShowNotificationDropdown(false)}
                    style={{fontSize:13, fontWeight:600, color:"#e85b28", textDecoration:"none"}}
                  >
                    {t("notifications.viewAll")} <i className="ti ti-arrow-right" style={{fontSize:13}}></i>
                  </Link>
                  {notifications && notifications.length > 0 && (
                    <button
                      style={{background:"none", border:"none", fontSize:12, color:"#94a3b8", cursor:"pointer", fontWeight:500, padding:0}}
                      onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.color = "#64748b"; }}
                      onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.color = "#94a3b8"; }}
                      onClick={(e) => {
                        e.preventDefault();
                        markAllAsRead();
                        notifications.forEach((notif) => { dismissNotification(notif.id); });
                      }}
                    >
                      {t("notifications.clearAll")}
                    </button>
                  )}
                </div>
              </div>
            </li>

            {/* Profile Dropdown */}

            <li
              className="nav-item dropdown has-arrow main-drop position-relative"
              ref={dropdownRef}
            >
              <Link
                to="#"
                className="nav-link userset"
                onClick={(e) => {
                  e.preventDefault();
                  setShowProfileDropdown(!showProfileDropdown);
                }}
              >
                <span className="user-info">
                  <span className="user-letter">
                    <ImageWithBasePath
                      src="assets/img/profiles/avatar-20.jpg"
                      alt="Profile"
                    />
                  </span>
                  <span className="badge badge-success rounded-pill" />
                </span>
              </Link>

              <div
                className={`dropdown-menu menu-drop-user ${showProfileDropdown ? "show" : ""
                  }`}
                style={{
                  position: "absolute",
                  right: 0,
                  top: "calc(100% + 5px)", // 5px below the toggle
                  margin: 0,
                }}
              >
                <div className="profilename">
                  {tenantName && (
                    <div className="dropdown-item disabled text-muted small" style={{ cursor: "default", opacity: 0.8 }}>
                      <i className="ti ti-building" /> {tenantName}
                    </div>
                  )}
                  <Link
                    onClick={() => {
                      setShowProfileDropdown(!showProfileDropdown);
                    }}
                    className="dropdown-item"
                    to={route.dealsDashboard}
                  >
                    <i className="ti ti-layout-2" /> {t("profile.dashboard")}
                  </Link>
                  <Link
                    onClick={() => {
                      setShowProfileDropdown(!showProfileDropdown);
                    }}
                    className="dropdown-item"
                    to={route.profile}
                  >
                    <i className="ti ti-user-pin" /> {t("profile.myProfile")}
                  </Link>
                  <Link
                    className="dropdown-item"
                    onClick={() => {
                      // Clears storage AND the live Redux store — storage
                      // alone leaves this tab's in-memory user/permissions
                      // state stale until a hard refresh (§5.1).
                      clearAuthToken();
                      dispatch(clearUser());
                      dispatch(clearPermissions());
                      setShowProfileDropdown(!showProfileDropdown);
                    }}
                    to={route.login}
                  >
                    <i className="ti ti-lock" /> {t("profile.logout")}
                  </Link>
                </div>
              </div>
            </li>
          </ul>
        </div>
        {/* Mobile Menu */}
        <div className="dropdown mobile-user-menu">
          <Link
            to="#"
            className="nav-link dropdown-toggle"
            data-bs-toggle="dropdown"
            aria-expanded="false"
          >
            <i className="fa fa-ellipsis-v" />
          </Link>
          <div className={` dropdown-menu `}>
            <Link className="dropdown-item" to={route.dealsDashboard}>
              <i className="ti ti-layout-2" /> {t("profile.dashboard")}
            </Link>
            <Link className="dropdown-item" to={route.profile}>
              <i className="ti ti-user-pin" /> {t("profile.myProfile")}
            </Link>
            <Link
              className="dropdown-item"
              onClick={() => {
                clearAuthToken();
                dispatch(clearUser());
                dispatch(clearPermissions());
              }}
              to={route.login}
            >
              <i className="ti ti-lock" /> {t("profile.logout")}
            </Link>
          </div>
        </div>
        {/* /Mobile Menu */}
      </div>
    </>
  );
};

export default Header

